<template>
  <div class="session-client" :class="{ 'embedded': isEmbedded }">
    <div v-if="!isEmbedded" class="join-page-top">
      <div class="room-header">
        <div class="room-header-top">
          <h1>Room ID: {{ roomId }}</h1>
          <div class="room-controls">
            <img
              v-if="!isMobile"
              src="../assets/computer.svg"
              alt="Computer"
              class="computer-icon"
              @click="openInDesktop"
              title="Open in desktop mode"
            >
            <button class="share-inline-toggle" @click="isShareExpanded = !isShareExpanded">
              <img src="@/assets/share.svg" alt="Share" class="icon" />
            </button>
            <google-cast-launcher></google-cast-launcher>
          </div>
        </div>

        <div v-if="userIdentity" class="user-identity-container">
          <div class="user-identity-label">You have joined as</div>
          <div class="user-identity" :style="{ background: userIdentity.userColor }">
            <div class="user-name">{{ userIdentity.userName }}</div>
            <div class="identity-actions">
              <button class="edit-name-button" @click="handleEditName" title="Edit display name">
                <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.33a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z"/></svg>
                <PremiumBadge v-if="!isPremium" size="small" />
              </button>
              <div v-if="!userIdentity.isCustomName" class="info-icon-wrapper">
                <button class="info-icon" @click="toggleNameInfo">&#8505;&#65039;</button>
                <div v-if="showNameInfo" class="info-tooltip">
                  <p>Your name is randomly generated when you join a room. This name is unique to this session and will change when the session is over or when you clear your browser data.</p>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isEditingName" class="edit-name-form">
            <input v-model="editNameValue" class="edit-name-input" placeholder="Enter your display name" maxlength="20" @keyup.enter="saveCustomName" @keyup.escape="cancelEditingName" />
            <div class="edit-name-actions">
              <button class="save-name-button" @click="saveCustomName" :disabled="isSavingName || !editNameValue.trim()">{{ isSavingName ? 'Saving...' : 'Save' }}</button>
              <button class="cancel-name-button" @click="cancelEditingName">Cancel</button>
            </div>
            <p v-if="editNameError" class="edit-name-error">{{ editNameError }}</p>
          </div>
        </div>
      </div>

      <div class="share-section" :class="{ expanded: isShareExpanded }">
        <div class="share-header" @click="isShareExpanded = !isShareExpanded">
          <h2>Share Room</h2>
          <span class="share-chevron">{{ isShareExpanded ? '▲' : '▼' }}</span>
        </div>
        <div class="share-body">
          <p class="instructions">Share link for others to join</p>
          <div class="url-section">
            <a :href="joinUrl" class="join-url">{{ joinUrl }}</a>
            <div class="url-actions">
              <button class="action-icon" @click="copyToClipboard" title="Copy link">
                <img src="@/assets/copy.svg" alt="Copy" class="icon" />
                <div v-if="copySuccess" class="copy-feedback">
                  Copied to clipboard
                </div>
              </button>
              <button class="action-icon" @click="shareUrl" title="Share">
                <img src="@/assets/share.svg" alt="Share" class="icon" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <button @click="showInstructions = true" class="help-button">
        How does it work?
      </button>
    </div>

    <!-- Remote Controller Component -->
    <RemoteController 
      :roomId="roomId" 
      :karaokePlayerRef="karaokePlayerRef" 
      :isEmbedded="isEmbedded"
      :playback-controls-target="playbackControlsTarget"
      :host-is-casting="hostIsCasting"
      :host-cast-session-available="hostCastSessionAvailable"
    />

    <!-- Instructions Modal -->
    <div v-if="showInstructions" class="modal-overlay" @click="showInstructions = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showInstructions = false">×</button>
        <h2>How does it work?</h2>
        <div class="instructions-content">
          <div class="instruction-step">
            <h3>1. Search & Add Songs</h3>
            <p>Search for your favorite karaoke songs using the search box and add them to the queue.</p>
          </div>
          <div class="instruction-step">
            <h3>2. Share with Friends</h3>
            <p>Share this room's code or link with friends so they can join and add songs to the queue together.</p>
          </div>
          <div class="instruction-step">
            <h3>3. Control Playback</h3>
            <p>Use the remote controls to play, pause, or skip to the next song in the queue.</p>
          </div>
          <div class="instruction-step">
            <h3>4. Cast to TV (Optional)</h3>
            <p>Optionally, click the Cast button to connect to your TV or Chromecast device for a bigger screen experience.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Add feedback link at the bottom -->
    <div v-if="!isEmbedded" class="footer">
      <button class="feedback-link" @click="showFeedbackModal = true">
        💡 Feedback or Suggestions
      </button>
    </div>

    <!-- FeedbackButton modal -->
    <FeedbackButton 
      :roomId="roomId" 
      v-if="showFeedbackModal"
      @close="showFeedbackModal = false"
    />

    <!-- Premium Upsell Modal -->
    <PremiumUpsellModal
      v-if="showUpsellModal"
      :feature="upsellFeature"
      @close="showUpsellModal = false"
      @require-sign-in="handleUpsellRequireSignIn"
    />
  </div>
</template>

<script>
import { useRoute } from 'vue-router'
import { ref, computed } from 'vue'
import FeedbackButton from './FeedbackButton.vue'
import { useAnalytics } from '../composables/useAnalytics'
import RemoteController from './RemoteController.vue'
import PremiumBadge from './PremiumBadge.vue'
import PremiumUpsellModal from './PremiumUpsellModal.vue'
import { useUserIdentity } from '../utils/userIdentity'
import { useAuth } from '../composables/useAuth'
import { useSubscription } from '../composables/useSubscription'

export default {
  name: 'SessionClient',
  components: {
    FeedbackButton,
    RemoteController,
    PremiumBadge,
    PremiumUpsellModal
  },
  props: {
    karaokePlayerRef: {
      type: Object,
      default: null
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    playbackControlsTarget: {
      type: Object,
      default: null
    },
    hostIsCasting: {
      type: Boolean,
      default: false
    },
    hostCastSessionAvailable: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const route = useRoute();
    const showInstructions = ref(false);
    const roomId = route.params.roomId;
    const joinUrl = `${window.location.origin}/join/${roomId}`;
    const { userIdentity } = useUserIdentity(roomId);
    const { updateCustomDisplayName, isLoggedIn } = useAuth();
    const { isPremium } = useSubscription();
    const isEditingName = ref(false);
    const editNameValue = ref('');
    const editNameError = ref(null);
    const isSavingName = ref(false);
    const showUpsellModal = ref(false);
    const upsellFeature = ref(null);
    const resumeUpsellAfterLogin = ref(false);
    const { trackEvent } = useAnalytics();

    const isMobile = computed(() => {
      return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    });

    return {
      roomId,
      joinUrl,
      showInstructions,
      isMobile,
      userIdentity,
      updateCustomDisplayName,
      isLoggedIn,
      isPremium,
      isEditingName,
      editNameValue,
      editNameError,
      isSavingName,
      showUpsellModal,
      upsellFeature,
      trackEvent,
      resumeUpsellAfterLogin
    }
  },
  data() {
    return {
      copySuccess: false,
      showFeedbackModal: false,
      isShareExpanded: false,
      showNameInfo: false,
      nameInfoTimeout: null
    }
  },
  watch: {
    isLoggedIn(newVal) {
      if (newVal && this.resumeUpsellAfterLogin) {
        this.showUpsellModal = true
        this.resumeUpsellAfterLogin = false
      }
    }
  },
  methods: {
    sendRoomId() {
      try {
        const context = cast.framework.CastContext.getInstance();
        const session = context.getCurrentSession();

        if (!session) {
          console.error('No active cast session');
          return;
        }

        console.log('Sending room ID:', this.roomId);

        session.sendMessage('urn:x-cast:com.google.cast.media', {
          type: 'ROOM_ID',
          customData: { roomId: this.roomId }
        }).then(() => {
          console.log('✅ Room ID sent:', this.roomId);
        }).catch(error => {
          console.error('❌ sendMessage failed:', error);
        });
      } catch (error) {
        console.error('Error in sendRoomId:', error);
      }
    },

    initializeCastApi() {
      if (!window.chrome || !window.chrome.cast || !window.cast) {
        console.log('Cast API not ready, waiting...');
        setTimeout(this.initializeCastApi, 1000);
        return;
      }
      
      try {
        const config = { CAST_APP_ID: process.env.VUE_APP_CAST_APP_ID || 'CC1AD845' };
        console.log('Initializing Cast API with App ID:', config.CAST_APP_ID);
        const context = cast.framework.CastContext.getInstance();
        context.setOptions({
          receiverApplicationId: config.CAST_APP_ID,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          language: 'en-US',
          resumeSavedSession: true
        });

        // Set up session listener
        const castChannel = new BroadcastChannel('cast-state');
        context.addEventListener(
          cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
          (event) => {
            console.log('Cast session state changed:', {
              oldState: event.oldState,
              sessionState: event.sessionState,
              errorCode: event.errorCode,
              details: {
                'NO_SESSION': 'No session',
                'SESSION_STARTING': 'Session is starting',
                'SESSION_STARTED': 'Session has started',
                'SESSION_START_FAILED': 'Session failed to start',
                'SESSION_ENDING': 'Session is ending',
                'SESSION_ENDED': 'Session has ended',
                'SESSION_RESUMED': 'Session was resumed'
              }[event.sessionState]
            });

            const self = this;
            switch (event.sessionState) {
              case cast.framework.SessionState.SESSION_STARTING:
              case cast.framework.SessionState.SESSION_STARTED:
              case cast.framework.SessionState.SESSION_RESUMED:
                console.log('Cast session active');
                self.isCasting = true;
                castChannel.postMessage({ 
                  type: 'CAST_STATE_CHANGE', 
                  isCasting: true,
                  sessionState: event.sessionState,
                  sessionId: context.getCurrentSession()?.getSessionId()
                });
                self.sendRoomId();
                break;
              case cast.framework.SessionState.SESSION_ENDED:
              case cast.framework.SessionState.SESSION_START_FAILED:
                console.log('Cast session ended or failed');
                self.isCasting = false;
                castChannel.postMessage({ 
                  type: 'CAST_STATE_CHANGE', 
                  isCasting: false,
                  sessionState: event.sessionState 
                });
                break;
            }
          }
        );

        // Also listen for cast state changes
        context.addEventListener(
          cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          event => {
            console.log('Cast state changed:', event);
            // Broadcast cast state change
            castChannel.postMessage({
              type: 'CAST_STATE_CHANGE',
              castState: event.castState
            });
          }
        );

        this.castInitialized = true;
        console.log('Cast API initialized successfully');
      } catch (error) {
        console.error('Error initializing Cast API:', error);
        setTimeout(this.initializeCastApi, 1000);
      }
    },

    async openInDesktop() {
      try {
        // Check for active cast session
        const context = cast.framework.CastContext.getInstance();
        const currentSession = context.getCurrentSession();

        if (currentSession) {
          // Prompt user about disconnecting
          const confirmDisconnect = window.confirm(
            'You currently have an active cast session. To ensure stable playback, ' +
            'you need to disconnect from the cast session before opening desktop mode. ' +
            'Would you like to disconnect and continue?'
          );

          if (!confirmDisconnect) {
            return; // User cancelled
          }

          // Disconnect from cast session
          await currentSession.endSession(true);
          console.log('Cast session ended successfully');

          // Log desktop mode switch with cast disconnect
          this.trackEvent('desktop_mode_switched', {
            room_id: this.roomId,
            cast_disconnected: true
          });
        } else {
          // Log desktop mode switch without cast disconnect
          this.trackEvent('desktop_mode_switched', {
            room_id: this.roomId,
            cast_disconnected: false
          });
        }

        // Get stored window name from localStorage or generate a new one
        const storageKey = `desktop_window_${this.roomId}`;
        let windowName = localStorage.getItem(storageKey);
        
        if (!windowName) {
          windowName = `room_${this.roomId}_${Date.now()}`;
          localStorage.setItem(storageKey, windowName);
        }

        const roomUrl = `/room/${this.roomId}`;
        window.open(roomUrl, windowName);
      } catch (error) {
        console.error('Error in openInDesktop:', error);
        alert('Failed to switch to desktop mode. Please try again.');
        // Log desktop mode switch error
        this.trackEvent('desktop_mode_switch_error', {
          room_id: this.roomId,
          error_message: error.message || 'Unknown error'
        });
      }
    },
    copyToClipboard() {
      const roomUrl = window.location.origin + '/join/' + this.roomId;
      const tempInput = document.createElement('input');
      tempInput.value = roomUrl;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      this.copySuccess = true;

      // Log URL copy
      this.trackEvent('room_url_copied', {
        room_id: this.roomId
      });

      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    },
    shareUrl() {
      const roomUrl = window.location.origin + '/join/' + this.roomId;
      const shareData = {
        title: 'Check out this karaoke room!',
        text: 'Join me in a fun karaoke session. Click the link to join.',
        url: roomUrl
      };

      // Log share attempt
      this.trackEvent('room_url_share_attempted', {
        room_id: this.roomId
      });

      if (navigator.share) {
        navigator.share(shareData)
          .then(() => {
            console.log('Shared successfully');
            // Log successful share
            this.trackEvent('room_url_shared', {
              room_id: this.roomId
            });
          })
          .catch(error => {
            console.error('Error sharing:', error);
            // Log share error
            this.trackEvent('room_url_share_error', {
              room_id: this.roomId,
              error_message: error.message || 'Unknown error'
            });
          });
      } else {
        console.error('Web Share API not supported');
        // Log share not supported
        this.trackEvent('room_url_share_not_supported', {
          room_id: this.roomId
        });
      }
    },
    toggleCast() {
      this.isCasting = !this.isCasting;
      if (this.isCasting) {
        this.requestCast();
      } else {
        console.log('Casting stopped');
      }
    },
    showBrandPrompt() {
      if (confirm('Are you sure you want to exit the Karaoke room?')) {
        window.location.href = '/';
      }
    },
    handleEditName() {
      if (!this.isPremium) {
        this.upsellFeature = 'displayName';
        this.showUpsellModal = true;
        return;
      }
      this.startEditingName();
    },
    startEditingName() {
      this.editNameValue = this.userIdentity.isCustomName ? this.userIdentity.userName : '';
      this.editNameError = null;
      this.isEditingName = true;
    },
    async saveCustomName() {
      if (this.isSavingName) return;
      this.isSavingName = true;
      this.editNameError = null;
      const result = await this.updateCustomDisplayName(this.editNameValue);
      if (result.success) {
        this.isEditingName = false;
      } else {
        this.editNameError = result.error;
      }
      this.isSavingName = false;
    },
    cancelEditingName() {
      this.isEditingName = false;
      this.editNameError = null;
    },
    toggleNameInfo() {
      this.showNameInfo = !this.showNameInfo;
      
      // Clear any existing timeout
      if (this.nameInfoTimeout) {
        clearTimeout(this.nameInfoTimeout);
      }
      
      // Set new timeout to auto-dismiss after 5 seconds
      if (this.showNameInfo) {
        this.nameInfoTimeout = setTimeout(() => {
          this.showNameInfo = false;
        }, 5000);
      }
    },
    handleUpsellRequireSignIn() {
      this.resumeUpsellAfterLogin = true
      this.showUpsellModal = false
      window.dispatchEvent(new Event('auth:open-sign-in'))
    },

    handleBackNavigation(event) {
      // Prevent the default back action
      if (event) {
        event.preventDefault();
      }
      
      // Show confirmation dialog
      if (confirm('Are you sure you want to leave the karaoke room?')) {
        // If confirmed, navigate to home
        window.location.href = '/';
      } else {
        // If cancelled, push a new state to maintain the current page
        window.history.pushState({ page: 'session' }, '', window.location.href);
      }
    },
    handleBeforeUnload(event) {
      // Cancel the event and show confirmation dialog
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
      return '';
    }
  },
  mounted() {
    console.log('SessionClient mounted');
    console.log('karaokePlayerRef prop:', this.karaokePlayerRef);
    
    this.trackEvent('room_visited', {
      room_id: this.roomId,
      surface: 'session_client'
    });
    
    this.initializeCastApi();

    // Handle back button and page unload
    window.addEventListener('popstate', this.handleBackNavigation);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    // Push initial state and replace current state to handle back button
    window.history.pushState({ page: 'session' }, '', window.location.href);
    window.history.replaceState({ page: 'session' }, '', window.location.href);
    
    // Check for active cast session on mount
    if (window.chrome && window.chrome.cast && window.cast) {
      const context = cast.framework.CastContext.getInstance();
      const currentSession = context.getCurrentSession();
      if (currentSession) {
        console.log('Found active cast session on mount');
        this.isCasting = true;
      }
    }
  },
  beforeUnmount() {
    // Remove navigation handlers
    window.removeEventListener('popstate', this.handleBackNavigation);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    
    // Clear timeout
    if (this.nameInfoTimeout) {
      clearTimeout(this.nameInfoTimeout);
    }
  }
}
</script>

<style scoped>
.session-client {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 60px);
  min-height: calc(100dvh - 60px);
  padding: 0.5rem;
  gap: 0.5rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%),
              radial-gradient(circle at top right, rgba(255, 165, 0, 0.1) 0%, transparent 60%),
              radial-gradient(circle at bottom left, rgba(255, 165, 0, 0.05) 0%, transparent 40%);
  background-blend-mode: normal, screen, screen;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow-y: auto;
}

.session-client.embedded {
  position: static;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
  padding: 1rem;
  background: transparent;
  gap: 0.75rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background-color: #1a1a1a;
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.emoji {
  font-size: 24px;
}

.brand {
  color: #ffa500;
  font-size: 18px;
  font-weight: 500;
  margin-right: 10px;
  cursor: pointer;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.room-info {
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.1);
  margin: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.room-id {
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.url-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.user-identity-container {
  margin: 0 20px 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-identity-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  padding-left: 4px;
}

.user-identity {
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.user-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.9);
}

.info-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.info-icon {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.info-icon:hover {
  opacity: 1;
  transform: scale(1.1);
}

.info-tooltip {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  width: 280px;
  max-width: calc(100vw - 60px);
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: tooltipFadeIn 0.2s ease-out;
}

.info-tooltip p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
}

.identity-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-name-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
  display: inline-flex;
  align-items: center;
  color: inherit;
}

.edit-name-button:hover {
  opacity: 1;
  transform: scale(1.1);
}

.edit-name-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.edit-name-input {
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.9rem;
}

.edit-name-input:focus {
  outline: none;
  border-color: #ffa500;
}

.edit-name-actions {
  display: flex;
  gap: 8px;
}

.save-name-button {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  background: #ffa500;
  color: black;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
}

.save-name-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-name-button {
  padding: 6px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  cursor: pointer;
}

.edit-name-error {
  color: #ff6b6b;
  font-size: 0.8rem;
  margin: 0;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-icon {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s;
  position: relative;
}

.action-icon:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Error Message Styles */
.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 0 20px;
  background: linear-gradient(135deg, #ff4444 0%, #cc0000 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 68, 68, 0.3);
  animation: slideIn 0.3s ease-out;
}

.error-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.error-text {
  flex: 1;
  line-height: 1.4;
}

.error-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.error-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Queue Status Indicator */
.queue-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  margin: 0 20px;
  background: linear-gradient(135deg, #ffa500 0%, #ff8c00 100%);
  color: white;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 165, 0, 0.3);
  animation: pulse 2s infinite;
}

.queue-status-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.queue-status-text {
  flex: 1;
  line-height: 1.4;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.icon {
  width: 18px;
  height: 18px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.action-icon:hover .icon {
  opacity: 1;
}

.copy-feedback {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  pointer-events: none;
  animation: fadeInOut 2s ease-in-out forwards;
  z-index: 1000;
  white-space: nowrap;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 1rem); }
  15% { opacity: 1; transform: translate(-50%, 0); }
  85% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -1rem); }
}

.help-button {
  background: none;
  border: none;
  color: #ffa500;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
  padding: 0.5rem;
  text-align: center;
  width: 100%;
  margin: -0.25rem 0;
}

.help-button:hover {
  color: #ff9500;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-content h2 {
  color: #ffa500;
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: 500;
}

.instructions-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.instruction-step h3 {
  color: white;
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
}

.instruction-step p {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media (max-width: 480px) {
  .modal-content {
    padding: 1.5rem;
  }
  
  .instruction-step h3 {
    font-size: 1rem;
  }
  
  .instruction-step p {
    font-size: 0.875rem;
  }
}

.computer-icon {
  width: 24px;
  height: 24px;
  filter: invert(1) opacity(0.7);
  cursor: pointer;
  transition: opacity 0.2s;
}

.computer-icon:hover {
  opacity: 1;
}

.search-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: calc(100vh - 160px);
  min-height: 0;
}

.search-box {
  display: flex;
  gap: 0.5rem;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.clear-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  transition: all 0.2s;
}

.clear-button:hover {
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: 400;
  transition: background-color 0.2s;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.search-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.15);
}

.search-button {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.search-button:hover {
  background: rgba(255, 165, 0, 0.2);
  transform: translateY(-1px);
}

.search-button:active {
  transform: translateY(0);
}

.search-button.loading,
.queue-button.loading,
.reserve-button.loading {
  pointer-events: none;
  opacity: 0.8;
}

.search-button.loading::after,
.queue-button.loading::after,
.reserve-button.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 25%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 100%
  );
  animation: shimmer 1s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.search-results {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: calc(100vh - 210px);
  -webkit-overflow-scrolling: touch;
}

.search-youtube-attribution {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 8px;
}

.search-youtube-logo {
  height: 20px;
  width: auto;
}

.search-youtube-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.search-attribution-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
}

.remote-control {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.remote-button {
  width: 120px;  /* Fixed width */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 20px;
  transition: all 0.2s;
  cursor: pointer;
}

.remote-button:hover {
  background: rgba(255, 165, 0, 0.2);
  transform: translateY(-1px);
}

.remote-button:active {
  transform: translateY(0);
}

.button-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
}

.video-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 0;
}

.video-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  text-decoration: none;
  color: white;
  min-width: 0;
  overflow: hidden;
}

.video-thumbnail {
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.video-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.video-info h3 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: 2.4em;
}

.video-info p {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reserve-button {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #ffa500;
  color: white;
  font-size: 1.125rem;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  margin-left: 0.25rem;
  cursor: pointer;
}

.reserve-button:hover {
  background: #ff9500;
  transform: translateY(-1px);
}

.reserve-button:active {
  transform: translateY(0);
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(118, 118, 128, 0.08);
  border-radius: 8px;
  min-width: 0;
  cursor: grab;
  transition: all 0.2s;
  position: relative;
}

.queue-item[draggable="true"]:active {
  cursor: grabbing;
}

.queue-item.not-draggable {
  cursor: default;
  opacity: 0.9;
}

.queue-item.dragging {
  opacity: 0.5;
  cursor: grabbing;
  transform: scale(0.95);
}

.queue-item.drag-over {
  border: 2px dashed #ffa500;
  background: rgba(255, 165, 0, 0.1);
}

/* Prevent drag-over styling on currently playing song */
.queue-item.not-draggable.drag-over {
  border: none;
  background: rgba(118, 118, 128, 0.08);
}

.drag-handle {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 1rem;
  cursor: grab;
  padding: 0.25rem;
  transition: color 0.2s;
  user-select: none;
}

.queue-item:hover .drag-handle {
  color: rgba(255, 255, 255, 0.7);
}

.queue-item.dragging .drag-handle {
  cursor: grabbing;
}

.queue-number {
  flex-shrink: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #8e8e93;
  min-width: 24px;
  text-align: center;
}

.queue-thumbnail {
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.queue-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.queue-info h3 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1c1c1e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: 2.4em;
}

.queue-info p {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #8e8e93;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-results, .loading {
  text-align: center;
  padding: 1rem;
  color: #8e8e93;
  font-weight: 500;
}

@media (max-width: 480px) {
  .session-client {
    padding: 0.25rem;
  }
  
  .header, .search-box, .remote-control {
    padding: 0rem;
  }
  
 .video-item, .queue-item {
    padding: 0.375rem;
  }
}

.overflow-menu {
  position: relative;
  margin-left: 0.25rem;
}

.overflow-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s;
}

.overflow-button:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.overflow-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.overflow-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.overflow-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
}

.overflow-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.overflow-close:hover {
  background: rgba(255, 255, 255, 0.1);
}

.overflow-item {
  display: block;
  padding: 0.5rem;
  color: white;
  text-decoration: none;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.overflow-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.overflow-item.delete-item {
  color: #ff3b30;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.25rem;
  padding-top: 0.75rem;
}

.overflow-item.delete-item:hover:not(:disabled) {
  background: rgba(255, 59, 48, 0.1);
}

.overflow-item.delete-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.5);
}

.overflow-item.delete-item:disabled:hover {
  background: none;
}

.channel-title {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #8e8e93;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(google-cast-launcher) {
  --connected-color: #ffa500;
  --disconnected-color: white;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s;
}

:deep(google-cast-launcher:hover) {
  background: rgba(255, 255, 255, 0.1);
}

.queue-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.95);
  border-left: none;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
}

.queue-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.queue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.queue-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
  color: white;
}

.queue-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.reorder-button {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.reorder-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Queue Loading States */
.queue-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: white;
  text-align: center;
}

.loading-spinner {
  font-size: 2rem;
  margin-bottom: 1rem;
  animation: spin 2s linear infinite;
}

.queue-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.queue-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.queue-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@keyframes slideIn {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* Queue item styles */
.queue-item {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.queue-info h3 {
  color: white;
}

.queue-info p {
  color: rgba(255, 255, 255, 0.7);
}

.queue-number {
  color: rgba(255, 255, 255, 0.5);
}

.channel-link {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
}

.channel-link:hover {
  text-decoration: underline;
}

.queue-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 0;
}

.queue-thumbnail {
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: 6px;
  object-fit: cover;
}

.queue-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.queue-info h3 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  max-height: 2.4em;
}

.channel-title {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.help-button {
  background: none;
  border: none;
  color: #ffa500;
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
  padding: 0.5rem;
  text-align: center;
  width: 100%;
  margin: -0.25rem 0;
}

.help-button:hover {
  color: #ff9500;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.modal-content {
  background: #1a1a1a;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-content h2 {
  color: #ffa500;
  margin: 0 0 1.5rem;
  font-size: 1.5rem;
  font-weight: 500;
}

.instructions-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.instruction-step h3 {
  color: white;
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
}

.instruction-step p {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

@media (max-width: 480px) {
  .modal-content {
    padding: 1.5rem;
  }
  
  .instruction-step h3 {
    font-size: 1rem;
  }
  
  .instruction-step p {
    font-size: 0.875rem;
  }
}

.computer-icon {
  width: 24px;
  height: 24px;
  filter: invert(1) opacity(0.7);
  cursor: pointer;
  transition: opacity 0.2s;
}

.computer-icon:hover {
  opacity: 1;
}

.footer {
  margin-top: auto;
  padding: 1rem;
  width: 100%;
  position: sticky;
  bottom: 0;
  background: #1a1a1a;
}

.feedback-link {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 0 auto;
}

.feedback-link:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.join-page-top {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.room-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem 1rem;
  margin: 0 20px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.room-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.room-header h1 {
  margin: 0;
  font-size: 1.2rem;
  color: white;
}

.room-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.share-inline-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.25rem;
  cursor: pointer;
  width: 36px;
  height: 36px;
}

.share-inline-toggle .icon {
  width: 18px;
  height: 18px;
  opacity: 0.8;
}

.share-section {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  margin: 0 20px;
}

.share-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
}

.share-header h2 {
  font-size: 1.1rem;
  font-weight: 500;
  color: #ffa500;
  margin: 0;
}

.share-chevron {
  display: none;
}

.share-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1.5rem 1rem;
}

.instructions {
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  font-size: 0.875rem;
  margin: 0;
}

.url-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.join-url {
  color: #ffa500;
  text-decoration: none;
  font-size: 0.875rem;
  word-break: break-all;
}

.join-url:hover {
  opacity: 0.8;
}

@media (max-width: 768px) {
  .session-client:not(.embedded) {
    overflow-y: auto;
  }

  :deep(.search-section) {
    max-height: none;
  }

  :deep(.search-results) {
    max-height: none;
    overflow-y: visible;
  }

  :deep(.my-music-section) {
    max-height: none;
    overflow-y: visible;
  }

  .room-header {
    margin: 0;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .room-header h1 {
    font-size: 0.95rem;
  }

  .share-inline-toggle {
    display: flex;
  }

  .share-header {
    display: none;
  }

  .share-section {
    display: none;
    margin: 0;
    border-radius: 8px;
  }

  .share-section.expanded {
    display: block;
  }

  .share-body {
    display: none;
    padding: 1rem;
  }

  .share-section.expanded .share-body {
    display: flex;
  }

  .user-identity-container {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    margin: 0;
  }

  .user-identity-label {
    font-size: 0.75rem;
    padding: 0;
    white-space: nowrap;
  }

  .user-identity {
    padding: 6px 12px;
    border-radius: 6px;
  }

  .user-name {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .session-client:not(.embedded) {
    padding: 0.75rem;
  }

  .join-page-top {
    gap: 0.5rem;
  }
}
</style>
